import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { PerformanceRatchetTier } from '@dealstack/frontend/shared/components/VestingTerms/PerformanceVesting/PerformanceRatchetTiers/PerformanceRatchetTier.tsx';
import { PerformanceRatchetTiersListStyles } from '@dealstack/frontend/shared/components/VestingTerms/PerformanceVesting/PerformanceRatchetTiersList/PerformanceRatchetTiersList.styles.tsx';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import {
	PerformanceVestingHurdleEntryDto,
	PerformanceVestingHurdleRes,
	PerformanceVestingMetricType,
} from '@dealstack/shared/lib/generated';
import { Button, Text } from '@mantine/core';
import { useState } from 'react';
import { TierFormValues } from '@dealstack/frontend/shared/components/VestingTerms/PerformanceVesting/PerformanceRatchetTiers/PerformanceRatchetTierEdit.types';

interface Props {
	tiers: PerformanceVestingHurdleRes[];
	metricType: PerformanceVestingMetricType;
	onSaveTiers: (tiers: PerformanceVestingHurdleEntryDto[]) => void;
}

const tiersToEntryDtos = (
	tiers: PerformanceVestingHurdleRes[]
): PerformanceVestingHurdleEntryDto[] =>
	tiers.map((tier) => ({
		moicThreshold: tier.moicThreshold ?? undefined,
		xirrThreshold: tier.xirrThreshold ?? undefined,
		incrementalPercentage: tier.incrementalPercentage,
	}));

export const PerformanceRatchetTiersList = ({ tiers, metricType, onSaveTiers }: Props) => {
	const tCommonUi = useTranslationCommonUi();
	const [isCreating, setIsCreating] = useState(false);

	const handleEditTier = (index: number, values: TierFormValues) => {
		const updated = [...tiers];
		updated[index] = {
			...updated[index],
			moicThreshold: values.moicThreshold,
			xirrThreshold: values.xirrThreshold,
			incrementalPercentage: values.incrementalPercentage,
		};
		onSaveTiers(tiersToEntryDtos(updated));
	};

	const handleDeleteTier = (index: number) => {
		const remaining = tiers.filter((_, i) => i !== index);
		onSaveTiers(tiersToEntryDtos(remaining));
	};

	const handleCreateTier = (values: TierFormValues) => {
		const newTier: PerformanceVestingHurdleEntryDto = {
			moicThreshold: values.moicThreshold ?? undefined,
			xirrThreshold: values.xirrThreshold ?? undefined,
			incrementalPercentage: values.incrementalPercentage,
		};
		onSaveTiers([...tiersToEntryDtos(tiers), newTier]);
		setIsCreating(false);
	};

	return (
		<PerformanceRatchetTiersListStyles>
			<Text fw={600} c="dark.8">
				{tCommonUi('vesting_terms.performance.tiers.title')}
			</Text>
			{tiers.map((tier, index) => (
				<div key={tier.id} className="tier-item">
					<PerformanceRatchetTier
						tier={tier}
						metricType={metricType}
						onSave={(values) => handleEditTier(index, values)}
						onDelete={() => handleDeleteTier(index)}
					/>
				</div>
			))}
			{isCreating ? (
				<div className="tier-item">
					<PerformanceRatchetTier
						metricType={metricType}
						isCreating
						onSave={handleCreateTier}
						onCancelCreate={() => setIsCreating(false)}
					/>
				</div>
			) : (
				<Button
					onClick={() => setIsCreating(true)}
					variant="ghost"
					leftSection={<Icon variant="plus" size="lg" />}
					className="add-button"
				>
					{tiers.length > 0
						? tCommonUi('vesting_terms.performance.tiers.add_another')
						: tCommonUi('vesting_terms.performance.tiers.add')}
				</Button>
			)}
		</PerformanceRatchetTiersListStyles>
	);
};
